/**
 * API-Facade — einzige Stelle fuer HTTP-Zugriffe (Muster rmnc-personal).
 *
 * Datenzugriff im UI ausschliesslich via TanStack Query ueber diese Facade;
 * ein Namespace pro Domain (api.reports.…, api.applications.…).
 */

import axios, { AxiosError } from "axios"
import { AUTH_MODE, getAccessToken, getDevUser } from "@/store/auth"
import type {
  AdminProvider,
  AdminProviderUpdate,
  AdminService,
  AdminServiceUpdate,
  ProviderTestErgebnis,
  Application,
  ChatNachricht,
  ModelInfo,
  Page,
  Report,
  ReportCreate,
  ReportDetail,
  ReportListParams,
  ReportUpdate,
  ReviewRequest,
  BackupLauf,
  BackupStatus,
  MeldungKommentar,
  NinoxUrlAufloesung,
  PraezisierungChatNachricht,
  PraezisierungVorschlag,
  SchemaCommit,
  SchemaUebersicht,
  Screenshot,
  ServiceTokenInfo,
  Tabelle,
  UsageZeile,
} from "@/types"

// Δ Ninox (U6): eigene Subdomain, BASE_URL ist "/" (Vite base) —
// die API liegt unter /api/v1 (Frontend-nginx proxyt zum Backend).
export const API_BASE_URL = `${import.meta.env.BASE_URL}api/v1`

const client = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
  // Array-Params als wiederholte Keys (?status=neu&status=gesichtet),
  // damit FastAPI sie als Liste parst — nicht als status[]=…
  paramsSerializer: { indexes: null },
})

// Request-Interceptor: Bearer-Token (Logto) bzw. X-Dev-User (Dev-Modus)
client.interceptors.request.use(async (config) => {
  if (AUTH_MODE === "dev") {
    config.headers["X-Dev-User"] = JSON.stringify(getDevUser())
    return config
  }
  const token = await getAccessToken()
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// Response-Interceptor: kein Auto-Logout bei 401 (Redirect-Loops vermeiden);
// Auth-State verwaltet der AuthProvider.
client.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => Promise.reject(error)
)

/** Deutsche Fehlermeldung aus einer Axios/FastAPI-Fehlerantwort extrahieren */
export function apiError(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const detail = (error.response?.data as { detail?: unknown } | undefined)?.detail
    if (typeof detail === "string") return detail
    if (error.response?.status === 403) return "Keine Berechtigung."
    if (error.response?.status === 401) return "Nicht angemeldet."
    if (error.response?.status === 413) return "Datei zu groß (Limit 10 MB)."
    // Kein Response: Chrome meldet u. a. ein nginx-413 bei HTTP/2 nur als
    // abgerissene Verbindung ("Network Error")
    if (!error.response)
      return "Netzwerkfehler — Verbindung unterbrochen (möglicherweise Datei zu groß)."
    return error.message
  }
  return "Unbekannter Fehler."
}

/** HTTP-Statuscode einer fehlgeschlagenen Anfrage (sonst null) */
export function apiStatus(error: unknown): number | null {
  return axios.isAxiosError(error) ? (error.response?.status ?? null) : null
}

export { client }

// =============================================================================
// DOMAIN-NAMESPACES
// =============================================================================

export const api = {
  // ---------------------------------------------------------------------------
  // Anwendungen (endpoints/applications.py, F2)
  // ---------------------------------------------------------------------------
  applications: {
    list: () => client.get<Application[]>("/applications").then((r) => r.data),
    // F8: Tabellen der Ninox-Anwendung für die Kontext-Auswahl
    tabellen: (applicationId: number) =>
      client.get<Tabelle[]>(`/applications/${applicationId}/tabellen`).then((r) => r.data),
    /** F8 (Δ Ninox): URL-Anker → Namen (Datenbank/Tabelle; DEV- und LIVE-URLs) */
    urlAufloesen: (
      applicationId: number,
      params: { team_id: string; db_id: string; tabelle_id?: string },
      signal?: AbortSignal
    ) =>
      client
        .get<NinoxUrlAufloesung>(`/applications/${applicationId}/ninox-url-aufloesung`, {
          params,
          signal,
        })
        .then((r) => r.data),
  },
  // ---------------------------------------------------------------------------
  // Meldungen (endpoints/reports.py, F4–F8, F19–F22)
  // ---------------------------------------------------------------------------
  reports: {
    list: (params?: ReportListParams) =>
      client.get<Page<Report>>("/reports", { params }).then((r) => r.data),
    /** Schnellsuche (Cmd/Ctrl-K): Titel, Beschreibungen, #Nummer */
    suche: (q: string, applicationId?: number, signal?: AbortSignal) =>
      client
        .get<Report[]>("/reports/suche", {
          params: { q, ...(applicationId != null ? { application_id: applicationId } : {}) },
          signal,
        })
        .then((r) => r.data),
    get: (id: number) => client.get<ReportDetail>(`/reports/${id}`).then((r) => r.data),
    /** Meldung beginnen — Status "entwurf" */
    create: (body: ReportCreate) => client.post<Report>("/reports", body).then((r) => r.data),
    update: (id: number, body: ReportUpdate) =>
      client.patch<Report>(`/reports/${id}`, body).then((r) => r.data),
    /** Eigenen Entwurf löschen */
    remove: (id: number) => client.delete<void>(`/reports/${id}`).then(() => undefined),
    /** Entwurf absenden → Status "neu" */
    absenden: (id: number) =>
      client.post<Report>(`/reports/${id}/absenden`).then((r) => r.data),
    /** F21: Kundenadmin verwirft (Begründung für Melder sichtbar) */
    verwerfen: (id: number, begruendung: string | null) =>
      client
        .post<Report>(`/reports/${id}/verwerfen`, { begruendung })
        .then((r) => r.data),
    /** F9/F10: Erfassungs-Dialog — Nachricht senden, KI-Antwort erhalten */
    chat: (id: number, inhalt: string) =>
      client.post<ChatNachricht>(`/reports/${id}/chat`, { inhalt }).then((r) => r.data),
    /** F37/F38 (PRD §5.7): Meldungs-Chat nach Freigabe (Melder ↔ Entwicklung) */
    kommentare: {
      list: (id: number) =>
        client.get<MeldungKommentar[]>(`/reports/${id}/kommentare`).then((r) => r.data),
      create: (id: number, text: string) =>
        client.post<MeldungKommentar>(`/reports/${id}/kommentare`, { text }).then((r) => r.data),
    },
    /** F13/S8 (Δ Ninox): Präzisierungs-Assistent — intern, nur Reviewer */
    praezisieren: (id: number) =>
      client.post<PraezisierungVorschlag>(`/reports/${id}/praezisieren`).then((r) => r.data),
    praezisierenChat: (id: number, nachricht: string, verlauf: PraezisierungChatNachricht[]) =>
      client
        .post<{ antwort: string }>(`/reports/${id}/praezisieren/chat`, { nachricht, verlauf })
        .then((r) => r.data.antwort),
    /** F14: Strukturierung + Einordnung → Mini-PRD-Vorschau (bleibt Entwurf) */
    finalize: (id: number) =>
      client.post<Report>(`/reports/${id}/finalize`).then((r) => r.data),
    /** F17: Reviewer — sichten/ablehnen/zusammenführen/in Umsetzung */
    review: (id: number, body: ReviewRequest) =>
      client.post<Report>(`/reports/${id}/review`, body).then((r) => r.data),
    /** F18: Freigabe erzeugt das Forgejo-Issue */
    freigeben: (id: number) =>
      client.post<Report>(`/reports/${id}/freigeben`).then((r) => r.data),
    /** F15: Export als Download (Header-Auth → Blob) */
    exportDownload: async (id: number, format: "markdown" | "json") => {
      const res = await client.get<Blob>(`/reports/${id}/export`, {
        params: { format },
        responseType: "blob",
      })
      const href = URL.createObjectURL(res.data)
      const anchor = document.createElement("a")
      anchor.href = href
      anchor.download = `meldung-${id}.${format === "json" ? "json" : "md"}`
      document.body.appendChild(anchor)
      anchor.click()
      anchor.remove()
      URL.revokeObjectURL(href)
    },
    screenshots: {
      /** PNG (Schwärzungen eingebrannt) + Vektor-Annotationen als JSON */
      upload: (reportId: number, bild: Blob, annotationen: unknown[]) => {
        const form = new FormData()
        form.append("file", bild, "screenshot.png")
        form.append("annotationen", JSON.stringify(annotationen))
        return client
          .post<Screenshot>(`/reports/${reportId}/screenshots`, form, {
            headers: { "Content-Type": "multipart/form-data" },
          })
          .then((r) => r.data)
      },
      remove: (reportId: number, screenshotId: number) =>
        client
          .delete<void>(`/reports/${reportId}/screenshots/${screenshotId}`)
          .then(() => undefined),
      /** Bilddatei als Blob — für die Nachbearbeitung im Annotation-Editor */
      datei: (reportId: number, screenshotId: number) =>
        client
          .get<Blob>(`/reports/${reportId}/screenshots/${screenshotId}/datei`, {
            responseType: "blob",
          })
          .then((r) => r.data),
      /**
       * Bilddatei als Object-URL laden — noetig, weil Auth per Header laeuft
       * (Bearer/X-Dev-User); ein <img src> wuerde den Header verlieren.
       * Aufrufer muss die URL mit URL.revokeObjectURL freigeben.
       */
      dateiUrl: (reportId: number, screenshotId: number) =>
        client
          .get<Blob>(`/reports/${reportId}/screenshots/${screenshotId}/datei`, {
            responseType: "blob",
          })
          .then((r) => URL.createObjectURL(r.data)),
    },
  },
  // ---------------------------------------------------------------------------
  // KI-Administration (endpoints/admin_ai.py, nur tester:reviewer)
  // ---------------------------------------------------------------------------
  admin: {
    providers: {
      list: () => client.get<AdminProvider[]>("/admin/ai-providers").then((r) => r.data),
      update: (provider: string, body: AdminProviderUpdate) =>
        client.put<AdminProvider>(`/admin/ai-providers/${provider}`, body).then((r) => r.data),
      models: (provider: string) =>
        client.get<ModelInfo[]>(`/admin/ai-providers/${provider}/models`).then((r) => r.data),
      /** Verbindungs-Test: feste Frage an den Anbieter, Antwort zurück */
      test: (provider: string) =>
        client
          .post<ProviderTestErgebnis>(`/admin/ai-providers/${provider}/test`)
          .then((r) => r.data),
    },
    services: {
      list: () => client.get<AdminService[]>("/admin/ai-services").then((r) => r.data),
      update: (serviceId: string, body: AdminServiceUpdate) =>
        client.put<AdminService>(`/admin/ai-services/${serviceId}`, body).then((r) => r.data),
      resetPrompt: (serviceId: string) =>
        client
          .post<AdminService>(`/admin/ai-services/${serviceId}/reset-prompt`)
          .then((r) => r.data),
    },
    usage: (monat?: string) =>
      client
        .get<UsageZeile[]>("/admin/ai-usage", { params: monat ? { monat } : undefined })
        .then((r) => r.data),
    /** Service-Token für die ERP-Anbindung (Anzeige/Kopieren, nur netz-fabrik) */
    serviceToken: () =>
      client.get<ServiceTokenInfo>("/admin/service-token").then((r) => r.data),
    moduleAktualisieren: (applicationId: number) =>
      client
        .post(`/admin/applications/${applicationId}/module/aktualisieren`)
        .then((r) => r.data),
    // -----------------------------------------------------------------------
    // Schema (endpoints/admin_schema.py, F29–F31 — nur tester:reviewer)
    // -----------------------------------------------------------------------
    schema: {
      uebersicht: (applicationId: number) =>
        client
          .get<SchemaUebersicht>(`/admin/applications/${applicationId}/schema`)
          .then((r) => r.data),
      datenbank: (applicationId: number, slug: string) =>
        client
          .get<{ markdown: string }>(
            `/admin/applications/${applicationId}/schema/datenbanken/${slug}`
          )
          .then((r) => r.data.markdown),
      historie: (applicationId: number, limit = 30) =>
        client
          .get<SchemaCommit[]>(`/admin/applications/${applicationId}/schema/historie`, {
            params: { limit },
          })
          .then((r) => r.data),
      analyse: (applicationId: number, name: string) =>
        client
          .get<{ markdown: string }>(
            `/admin/applications/${applicationId}/schema/analysen/${name}`
          )
          .then((r) => r.data.markdown),
    },
    // Schema-Backup-Dienst (F30) — Antworten kommen 1:1 vom Dienst (snake_case)
    schemaBackup: {
      status: () => client.get<BackupStatus>("/admin/schema-backup/status").then((r) => r.data),
      runs: (limit = 20) =>
        client
          .get<BackupLauf[]>("/admin/schema-backup/runs", { params: { limit } })
          .then((r) => r.data),
      run: () => client.post<{ run_id: string }>("/admin/schema-backup/run").then((r) => r.data),
    },
  },
}
